import {
  getColorFamilyProducts,
  getSwatchLabel,
  getSwatchColor,
  getSwatchImage,
  getColorPatternLabel,
  getProductBadges,
} from "../global/storefront/color-family";

function numericIdFromGid(gid) {
  const match = typeof gid === "string" && gid.match(/(\d+)$/);

  return match ? match[1] : null;
}

class WishlistDrawer extends HTMLElement {
  constructor() {
    super();
    this.closeButton = this.querySelector("[data-close]");
    this.content = this.querySelector(".wishlist-drawer__content");
    this.titleEl = this.querySelector("[data-title]");
    this.emptyEl = this.querySelector("[data-empty]");
    this.pickerEl = this.querySelector("[data-picker]");
    this.imageEl = this.querySelector("[data-image]");
    this.colorRowEl = this.querySelector("[data-color-row]");
    this.colorNameEl = this.querySelector("[data-color-name]");
    this.badgesEl = this.querySelector("[data-badges]");
    this.swatchesEl = this.querySelector("[data-swatches]");
    this.addButton = this.querySelector("[data-add]");

    this.colorFamily = [];
    this.pendingOnAdd = null;
    this.pendingOnSelect = null;
    this.pendingOnColorChange = null;
    this.defaultAddLabel = this.addButton?.textContent.trim() || "";
    this.activeProductId = null;

    this.closeButton?.addEventListener("click", () => this._close());
    this.addEventListener("click", (e) => {
      if (e.target.closest(".wishlist-drawer__content")) return;

      this._close();
    });

    this.handleDocumentClick = (e) => {
      if (!document.body.contains(e.target)) return;

      if (!this.content.contains(e.target)) {
        this._close();
      }
    };

    document.addEventListener("click", this.handleDocumentClick);

    this.closeEsc = (e) => {
      if (e.key === "Escape") {
        this._close();
      }
    };

    document.addEventListener("keydown", this.closeEsc);

    this.handleOpen = (e) => this._open(e.detail || null);
    this.handleClose = () => this._close();

    document.addEventListener("wishlist:open", this.handleOpen);
    document.addEventListener("wishlist:close", this.handleClose);

    document.addEventListener("drawer:before-open", (e) => {
      if (e.detail?.name !== "wishlist") this._close();
    });

    this.addButton?.addEventListener("click", () => this._handleAdd());
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.closeEsc);
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("wishlist:open", this.handleOpen);
    document.removeEventListener("wishlist:close", this.handleClose);
  }

  _toggle() {
    this.hasAttribute("open") ? this._close() : this._open();
  }

  _renderImage(image, imageAlt) {
    if (!this.imageEl) return;

    // an empty src makes the browser fetch the page itself as an image and draw a broken icon, so
    // the element is hidden when there is nothing to show
    if (!image) {
      this.imageEl.removeAttribute("src");
      this.imageEl.alt = "";
      this.imageEl.hidden = true;
      return;
    }

    this.imageEl.hidden = false;
    this.imageEl.src = image;
    this.imageEl.alt = imageAlt || "";
  }

  _renderColorName(color) {
    if (this.colorNameEl) {
      this.colorNameEl.textContent = color || "";
    }

    if (this.colorRowEl) {
      this.colorRowEl.hidden = !color;
    }
  }

  _renderBadges(badges) {
    if (!this.badgesEl) return;

    this.badgesEl.innerHTML = "";

    (badges || []).forEach((badge) => {
      const span = document.createElement("span");

      span.className = "badge";
      span.textContent = badge;

      this.badgesEl.appendChild(span);
    });

    this.badgesEl.style.display = badges?.length ? "" : "none";
  }

  _renderSwatches(products, activeProductId) {
    if (!this.swatchesEl) return;

    this.swatchesEl.innerHTML = "";

    if (!products.length) {
      this.swatchesEl.hidden = true;
      return;
    }

    this.swatchesEl.hidden = false;

    products.forEach((product) => {
      const swatch = document.createElement("button");
      const label = getSwatchLabel(product);
      const color = getSwatchColor(product);
      const image = getSwatchImage(product);

      swatch.type = "button";
      swatch.className = "wishlist-drawer__swatch";
      swatch.setAttribute("aria-label", label ? `Select ${label}` : "Select color");
      swatch.title = label;

      if (color) {
        swatch.style.backgroundColor = color;
      }

      if (image) {
        swatch.style.backgroundImage = `url("${image}")`;
      }

      const isActive = product.id === activeProductId;

      swatch.classList.toggle("is-active", isActive);
      swatch.setAttribute("aria-pressed", String(isActive));

      swatch.addEventListener("click", () => this._selectColor(product));

      this.swatchesEl.appendChild(swatch);
    });
  }

  _selectColor(product) {
    this.activeProductId = product.id;

    this._renderImage(product.featuredImage?.url, product.featuredImage?.altText);
    this._renderColorName(getColorPatternLabel(product) || getSwatchLabel(product));
    this._renderBadges(getProductBadges(product));

    this.swatchesEl?.querySelectorAll(".wishlist-drawer__swatch").forEach((swatch, index) => {
      const isActive = this.colorFamily[index]?.id === product.id;

      swatch.classList.toggle("is-active", isActive);
      swatch.setAttribute("aria-pressed", String(isActive));
    });

    this._applySwymAttributes({
      productId: numericIdFromGid(product.id),
      variantId: numericIdFromGid(product.variants?.nodes?.[0]?.id),
      url: product.handle ? `${window.location.origin}/products/${product.handle}` : "",
    });

    this.pendingOnColorChange?.(product);

    this._refreshDoneState();
  }

  // Relabels the button for the shown color: added already → doneLabel.
  _setDone(done) {
    this.selectionDone = done;
    if (this.addButton) this.addButton.textContent = done ? this.doneLabel : this.addLabel;
  }

  async _refreshDoneState() {
    if (!this.pendingIsDone || !this.addButton) return;

    const selection = { ...this.addButton.dataset };
    this._setDone(false);

    const done = await Promise.resolve(this.pendingIsDone(selection)).catch(() => false);
    // Ignore an answer for a color that's no longer shown.
    if (this.addButton.dataset.productId === selection.productId) this._setDone(!!done);
  }

  // Swym only binds its own markup, so the save is made through the SDK rather than relying on
  // the data-swaction attribute being picked up.
  _saveToSwym(onSaved, onFailed) {
    const { productId, variantId, productUrl } = this.addButton?.dataset || {};

    if (!productId || !variantId) {
      onFailed?.();
      return;
    }

    const item = {
      epi: Number(variantId),
      empi: Number(productId),
      du: productUrl,
      qty: 1,
    };

    // Every Swym call needs its error callback: the SDK invokes it unconditionally on a failed
    // response, so omitting it throws inside Swym instead of reporting the failure back here.
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      const add = (listId) =>
        swat.addToList(
          listId,
          item,
          () => {
            document.dispatchEvent(new CustomEvent("wishlist:updated", { bubbles: true }));
            onSaved?.();
          },
          onFailed,
        );

      swat.fetchLists({
        callbackFn: (lists) => {
          const existing = lists?.[0];

          if (existing) {
            add(existing.lid);
            return;
          }

          swat.createList({ lname: "My Wishlist" }, (newList) => add(newList.lid), onFailed);
        },
        errorFn: onFailed,
      });
    });
  }

  // The Add button is a single static element shared by every product the drawer opens for, so
  // it must be re-targeted at whichever product/variant is currently shown before Swym can act on it.
  _applySwymAttributes({ productId, variantId, url }) {
    if (!this.addButton) return;

    if (productId) {
      this.addButton.dataset.productId = productId;
    } else {
      delete this.addButton.dataset.productId;
    }

    if (variantId) {
      this.addButton.dataset.variantId = variantId;
    } else {
      delete this.addButton.dataset.variantId;
    }

    if (url) {
      this.addButton.dataset.productUrl = url;
    } else {
      delete this.addButton.dataset.productUrl;
    }
  }

  // Waits for the color-family fetch (when there is one) before showing anything, so the title,
  // image, color name and swatches all appear together instead of the swatches popping in after
  // everything else once the request resolves.
  async _open(detail) {
    if (this.hasAttribute("open")) return;

    document.dispatchEvent(new CustomEvent("markup-modal:close"));

    const hasProduct = Boolean(detail?.productId);

    // Tracks which _open() call is current so a color-family fetch that resolves after the
    // drawer has since been closed or reopened for another product can detect it's stale and bail.
    this._openToken = (this._openToken || 0) + 1;
    const token = this._openToken;

    let colorFamily = [];

    if (hasProduct && detail.productGroupId) {
      try {
        colorFamily = await getColorFamilyProducts(detail.productGroupId, detail.productId);
      } catch (error) {
        console.error("[Wishlist] Failed to load color options:", error);
      }

      if (token !== this._openToken) return;
    }

    if (this.titleEl) {
      this.titleEl.textContent = hasProduct ? this.titleEl.dataset.titleSelect : this.titleEl.dataset.titleWishlist;
    }

    if (this.emptyEl) this.emptyEl.hidden = hasProduct;
    if (this.pickerEl) this.pickerEl.hidden = !hasProduct;

    this.pendingOnColorChange = hasProduct && typeof detail.onColorChange === "function" ? detail.onColorChange : null;

    if (hasProduct) {
      this.pendingOnAdd = detail.onAdd || null;

      // Another feature can borrow the color picker (e.g. Add to Showroom):
      // `onSelect` receives the chosen product/variant instead of a Swym save,
      // and `addLabel` relabels the button.
      this.pendingOnSelect = typeof detail.onSelect === "function" ? detail.onSelect : null;
      this.addLabel = detail.addLabel || this.defaultAddLabel;
      if (this.addButton) this.addButton.textContent = this.addLabel;

      // Optional "done" state for a borrower that keeps the drawer open
      // (`keepOpen`): `isDone(selection)` says whether the shown color is
      // already added; then the button reads `doneLabel` and runs `onDone`.
      this.keepOpen = !!detail.keepOpen;
      this.pendingIsDone = typeof detail.isDone === "function" ? detail.isDone : null;
      this.doneLabel = detail.doneLabel || "";
      this.pendingOnDone = typeof detail.onDone === "function" ? detail.onDone : null;
      this.selectionDone = false;
      this.activeProductId = detail.productId;
      this.colorFamily = colorFamily;

      this._applySwymAttributes({
        productId: detail.swymProductId,
        variantId: detail.swymVariantId,
        url: detail.swymProductUrl,
      });

      // The colour family already carries a full-size image, the colour label and the badges for
      // every colourway, so the opening render uses the same source the swatches do rather than
      // whatever the caller scraped off the page. Values passed in are only a fallback.
      const current = colorFamily.find((product) => product.id === detail.productId);

      if (current) {
        this._renderImage(current.featuredImage?.url, current.featuredImage?.altText);
        this._renderColorName(getColorPatternLabel(current) || getSwatchLabel(current));
        this._renderBadges(getProductBadges(current));
      } else {
        this._renderImage(detail.image, detail.imageAlt);
        this._renderColorName(detail.color);
        this._renderBadges(detail.badges);
      }

      this._renderSwatches(colorFamily, detail.productId);
      this._refreshDoneState();
    }

    this.style.display = "block";
    window.requestAnimationFrame(() => {
      this.setAttribute("open", "");

      // on mobile the page stays visible behind the sheet, so the card it was opened from can be
      // seen following the colour picked here
      if (window.matchMedia(`(min-width: ${window.themeBreakpoints["tablet-wide"]}px)`).matches) {
        window.openFullDimmer(this);
      }
      window.lockScroll(this);
      this.returnFocusEl = document.activeElement;
      window.trapFocus(this, this.closeButton);
    });
  }

  _close() {
    if (!this.hasAttribute("open")) return;

    this._openToken = (this._openToken || 0) + 1;

    this.removeAttribute("open");
    window.closeFullDimmer(this);

    const onEnd = () => {
      this.removeEventListener("transitionend", onEnd);

      if (this.hasAttribute("open")) return;
      this.style.display = null;
      window.removeTrapFocus(this.returnFocusEl);
      window.unlockScroll(this);
    };
    this.addEventListener("transitionend", onEnd);
  }

  async _handleAdd() {
    if (!this.pendingOnSelect) {
      this._addToWishlist();
      return;
    }

    const { productId, variantId } = this.addButton?.dataset || {};

    if (this.selectionDone) {
      this.pendingOnDone?.({ productId, variantId });
      return;
    }

    if (!this.keepOpen) {
      this.pendingOnSelect({ productId, variantId });
      this._close();
      return;
    }

    // Stays open: the button becomes doneLabel once the add succeeds.
    this.addButton.disabled = true;
    this.addButton.classList.add("is-loading");
    const ok = await Promise.resolve(this.pendingOnSelect({ productId, variantId })).catch(() => false);
    this.addButton.disabled = false;
    this.addButton.classList.remove("is-loading");

    if (ok && this.addButton.dataset.productId === productId) this._setDone(true);
  }

  // The heart and the confirmation wait for Swym to confirm, so a failed save is never reported
  // to the customer as a successful one.
  _addToWishlist() {
    const onAdd = this.pendingOnAdd;

    this._saveToSwym(
      () => {
        onAdd?.();
        window.showToast(window.localeStrings?.wishlist?.added || "Added to wishlist");
      },
      () => {
        window.showToast(window.localeStrings?.wishlist?.error || "Could not add to wishlist");
      },
    );

    this._close();
  }
}

if (!customElements.get("wishlist-drawer")) {
  customElements.define("wishlist-drawer", WishlistDrawer);
}
